import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, Alert, CircularProgress } from '@mui/material';
import ModalWrapper from '../components/ModalWrapper';
import ShoppingListGroceryForm from '../forms/ShoppingListGroceryForm';
import { ShoppingListGrocery, ShoppingListGroceryFormData, emptyShoppingListGroceryFormData } from '../types/ShoppingListGrocery';
import { Grocery } from '../types/Grocery';
import { apiFetch } from '../apiFetch';

interface ShoppingListGroceryFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (item: ShoppingListGrocery) => void;
  shoppingListId: number;
  existingGroceryIds: number[];
  item?: ShoppingListGrocery | null;
}

const ShoppingListGroceryFormModal: React.FC<ShoppingListGroceryFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  shoppingListId,
  existingGroceryIds,
  item,
}) => {
  const [formData, setFormData] = useState<ShoppingListGroceryFormData>(emptyShoppingListGroceryFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof ShoppingListGroceryFormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [availableGroceries, setAvailableGroceries] = useState<Grocery[]>([]);
  const [loadingGroceries, setLoadingGroceries] = useState(true);

  const isEditing = !!item;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch available groceries
  useEffect(() => {
    if (!open) return;

    const fetchGroceries = async () => {
      setLoadingGroceries(true);
      try {
        const data = await apiFetch(`${apiUrl}/api/groceries/`);
        // Filter out groceries already in the list (unless editing that specific item)
        const filtered = data.filter((g: Grocery) =>
          !existingGroceryIds.includes(g.id) || (item && item.grocery === g.id)
        );
        setAvailableGroceries(filtered);
      } catch (err: any) {
        setApiError(err.message || 'Failed to load groceries.');
      } finally {
        setLoadingGroceries(false);
      }
    };
    fetchGroceries();
  }, [apiUrl, open, existingGroceryIds, item]);

  // Populate form when editing
  useEffect(() => {
    if (item) {
      setFormData({
        grocery: item.grocery.toString(),
        quantity: item.quantity.toString(),
      });
    } else {
      setFormData(emptyShoppingListGroceryFormData);
    }
    setErrors({});
    setApiError(null);
  }, [item, open]);

  const handleChange = (field: keyof ShoppingListGroceryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ShoppingListGroceryFormData, string>> = {};
    if (!formData.grocery) {
      newErrors.grocery = 'Please select a grocery';
    }
    const qty = parseInt(formData.quantity, 10);
    if (!formData.quantity || isNaN(qty) || qty < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    const payload = {
      grocery: parseInt(formData.grocery, 10),
      quantity: parseInt(formData.quantity, 10),
    };

    try {
      let savedItem: ShoppingListGrocery;
      if (isEditing && item) {
        savedItem = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingListId}/groceries/${item.id}/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        savedItem = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingListId}/groceries/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      onSuccess(savedItem);
      onClose();
    } catch (err: any) {
      setApiError(err.message || 'An error occurred while saving.');
    } finally {
      setLoading(false);
    }
  };

  const noGroceriesAvailable = !loadingGroceries && availableGroceries.length === 0 && !isEditing;

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edit Grocery Quantity' : 'Add Grocery to List'}
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      {loadingGroceries ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : noGroceriesAvailable ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          All your groceries are already in this list. Add more groceries in the Groceries section first.
        </Alert>
      ) : (
        <ShoppingListGroceryForm
          formData={formData}
          onChange={handleChange}
          errors={errors}
          availableGroceries={availableGroceries}
          isEditing={isEditing}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || noGroceriesAvailable}
        >
          {loading ? <CircularProgress size={24} /> : isEditing ? 'Save Changes' : 'Add to List'}
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default ShoppingListGroceryFormModal;